import type { AxiosError, AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import type {
  UseQueryResult,
  UseQueryOptions,
  UseMutationResult,
  UseMutationOptions,
  QueryFunctionContext,
} from '@tanstack/react-query';

import axios from 'axios';
import { useQuery, isServer, QueryClient, useMutation } from '@tanstack/react-query';

import defaultAxios from 'src/lib/axios';

import { queryClientBaseConfig } from './react-query.config';
import { fetcherClientTransformer } from './fetcher.transformer';

import type { BaseArgs, BaseError, ExtraOptions } from './fetcher.types';

export type PrefetchReturnType = () => Promise<QueryClient>;

export const fetcherClient = async <T>(args: BaseArgs, extraOptions: ExtraOptions): Promise<T> => {
  const { customBaseUrl, enableLogs = false, withBaseUrl = true, token } = extraOptions;
  // For internal traffic (defaultAxios) we rely on the shared interceptor to attach the ID token.
  // Only attach explicit tokens when hitting a custom base URL.
  const shouldInjectAuthHeader = Boolean(token && (!withBaseUrl || customBaseUrl));

  const headers: AxiosRequestConfig['headers'] = {
    ...(shouldInjectAuthHeader && { Authorization: `Bearer ${token}` }),
    ...(args.headers ?? {}),
  };
  const instanceConfig: AxiosRequestConfig = { ...args, headers };
  const { url, method = 'GET', params, data } = instanceConfig;

  const axiosInstance: AxiosInstance = withBaseUrl && !customBaseUrl
    ? defaultAxios
    : axios.create({
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        ...(withBaseUrl && { baseURL: customBaseUrl }),
      });

  try {
    const response = await axiosInstance
      .request({
        ...instanceConfig,
      })
      .then((apiResponse: AxiosResponse<T>) => apiResponse)
      .catch((e: AxiosError) => {
        if (process.env.NODE_ENV !== 'production') console.error('Error!', e);

        return Promise.reject(e);
      });

    return response.data;
  } catch (e) {
    throw fetcherClientTransformer.transformError(e as AxiosError);
  }
};

export const fetcher = <TData>(
  { queryKey, pageParam }: Pick<QueryFunctionContext<QueryKeyT>, 'queryKey' | 'pageParam'>,
  extraOptions?: ExtraOptions
): Promise<TData> => {
  const [url, params] = queryKey;

  return fetcherClient<TData>(
    {
      params: { ...params, pageParam },
      url,
    },
    extraOptions || {}
  );
};

export type QueryKeyT = [string, object | null];

export const useFetch = <TData>({
  params = null,
  config,
  url,
}: {
  params?: object | null;
  config?: UseQueryOptions<TData, BaseError, TData, QueryKeyT>;
  url: string | null;
}): UseQueryResult<TData, BaseError> =>
  useQuery<TData, BaseError, TData, QueryKeyT>({
    queryKey: [url!, params],
    queryFn: ({ queryKey }) => fetcher<TData>({ queryKey }),
    enabled: !!url,
    ...config,
  });

export const ssrPrefetch = async <TData>({
  params = null,
  url,
}: {
  params?: object | null;
  url: string | null;
}): Promise<QueryClient> => {
  const queryClient = getQueryClient();

  if (!url) return queryClient;

  await queryClient.prefetchQuery<TData, BaseError, TData, QueryKeyT>({
    queryKey: [url, params!],
    queryFn: ({ queryKey }) => fetcher<TData>({ queryKey }),
  });
  return queryClient;
};

function makeQueryClient() {
  return new QueryClient({ ...queryClientBaseConfig });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

//mutations
type MutationContext = unknown;

const useGenericMutation = <TData, TVariables, TContext = MutationContext>({
  options,
  updater,
  params,
  func,
  url,
}: {
  options?: Omit<UseMutationOptions<TData, BaseError, TVariables, TContext>, 'mutationFn'>;
  updater?: ((oldData: TVariables | undefined, newData: TVariables) => TVariables) | undefined;
  params?: object;
  func: (data: TVariables) => Promise<TData>;
  url: string;
}): UseMutationResult<TData, BaseError, TVariables, TContext> => {
  const queryClient = getQueryClient();

  return useMutation<TData, BaseError, TVariables, TContext>({
    mutationFn: func,
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [url, params!] });

      const previousData = queryClient.getQueryData([url, params]);

      queryClient.setQueryData<TVariables>([url, params], (oldData) =>
        updater ? updater(oldData, data) : data
      );

      return previousData as Awaited<TContext>;
    },
    onError: (error, variables, onMutateResult, mutationContext) => {
      queryClient.setQueryData([url, params], onMutateResult);
      options?.onError?.(error, variables, onMutateResult, mutationContext);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [url, params!] });
    },
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
    ...options,
  });
};

export const usePost = <TVariables, TData, TContext = MutationContext>({
  extraOptions,
  options,
  updater,
  params,
  url,
}: {
  extraOptions?: ExtraOptions;
  options?: Omit<UseMutationOptions<TData, BaseError, TVariables, TContext>, 'mutationFn'>;
  updater?: (oldData: TVariables | undefined, newData: TVariables) => TVariables;
  params?: object;
  url: string;
}): UseMutationResult<TData, BaseError, TVariables, TContext> =>
  useGenericMutation<TData, TVariables, TContext>({
    options,
    updater,
    params,
    func: async (data) =>
      await fetcherClient<TData>(
        {
          method: 'POST',
          data,
          url,
        },
        extraOptions || {}
      ),
    url,
  });
